/**
 * Knowledge Architecture Types
 *
 * Types for the tier detection, FAIR dimension scoring,
 * and knowledge architecture visualization.
 */

// =============================================================================
// KNOWLEDGE TIER
// =============================================================================

export type KnowledgeTier = 'taxonomy' | 'ontology' | 'knowledge_graph';

export interface TierSignal {
  label: string;
  met: boolean;
  value?: string;
}

export interface TierDetectionResult {
  tier: KnowledgeTier;
  confidence: 'high' | 'medium' | 'low';
  signals: TierSignal[];
  nextTierActions: string[];
}

// =============================================================================
// FAIR DIMENSION SCORES
// =============================================================================

export interface FairDimensionScores {
  findability: number;   // 0-100
  accessibility: number; // 0-100
  interoperability: number; // 0-100
  reusability: number;  // 0-100
}

// =============================================================================
// ONTOLOGY STATISTICS (mirrors OntologyBrowser)
// =============================================================================

export interface OntologyStatistics {
  term_count: number;
  class_count: number;
  property_count: number;
  individual_count: number;
  hierarchy_depth: number;
  max_breadth: number;
  triple_count: number;
  namespace_count: number;
}

// =============================================================================
// STRUCTURAL ANALYSIS (subset used by this module)
// =============================================================================

export interface PropertyCoverage {
  count: number;
  total: number;
  percentage: number;
  severity: 'ok' | 'warning' | 'critical';
}

export interface AnnotationCoverageStat {
  count: number;
  total: number;
  percentage: number;
}

export interface AnnotationCoverage {
  rdfsLabel: AnnotationCoverageStat;
  rdfsComment: AnnotationCoverageStat;
  skosDefinition: AnnotationCoverageStat;
  toolingWarning?: string;
}

export interface ShaclConsistencyAnalysis {
  undeclaredProperties: unknown[];
  phantomClasses: unknown[];
  malformedIRIs: unknown[];
  dualTypedClasses: number;
  blankNodeCount: number;
}

export interface DisjointnessAnalysis {
  siblingGroups: Array<{
    parent: string;
    siblings: string[];
    hasDisjointness: boolean;
  }>;
  missingDisjointnessCount: number;
}

export interface ControlledVocabularyAnalysis {
  candidates: unknown[];
  totalStringProperties: number;
  candidateCount: number;
  severity: 'ok' | 'warning' | 'critical';
}

export interface StructuralAnalysisData {
  score: number;
  propertiesWithDomain: PropertyCoverage;
  propertiesWithRange: PropertyCoverage;
  hierarchyDepth: { value: number; assessment: string; severity: string };
  inverseProperties: { count: number; severity: string };
  externalNamespaces: string[];
  orphanClasses: string[];
  classPropertyRatio: number;
  propertyTypeDistribution: {
    object: number;
    datatype: number;
    annotation: number;
    objectPercent: number;
    datatypePercent: number;
    annotationPercent: number;
  };
  hierarchyBreadth: Record<string, number>;
  controlledVocabularyIssues?: ControlledVocabularyAnalysis;
  shaclConsistency?: ShaclConsistencyAnalysis;
  annotationCoverage?: AnnotationCoverage;
  disjointnessAnalysis?: DisjointnessAnalysis;
  analyzedAt: string;
}

// =============================================================================
// TIER INFO (educational content)
// =============================================================================

export interface TierInfo {
  tier: KnowledgeTier;
  label: string;
  definition: string;
  compassRole: string;
  capabilities: string[];
  pharmaExamples: string[];
  fairImpact: string;
}

export const TIER_INFO: Record<KnowledgeTier, TierInfo> = {
  taxonomy: {
    tier: 'taxonomy',
    label: 'Taxonomy',
    definition: 'A hierarchical classification system with parent-child (is-a) relationships. Organizes concepts into a tree structure without formal semantics.',
    compassRole: 'DATA Compass validates hierarchy structure, checks annotation coverage, and detects orphan classes.',
    capabilities: ['Hierarchy validation', 'Label coverage', 'Orphan detection'],
    pharmaExamples: [
      'MedDRA (Medical Dictionary for Regulatory Activities)',
      'ATC (Anatomical Therapeutic Chemical Classification)',
      'WHO Drug Dictionary',
      'CDISC Terminology',
    ],
    fairImpact: 'Taxonomies provide basic findability through hierarchical categorization, but limited interoperability without formal property definitions.',
  },
  ontology: {
    tier: 'ontology',
    label: 'Ontology',
    definition: 'A formal specification of concepts with typed properties, domain/range constraints, and axioms. Enables semantic reasoning and validation.',
    compassRole: 'DATA Compass runs full structural analysis including domain/range coverage, SHACL consistency, disjointness axioms, and controlled vocabulary detection.',
    capabilities: ['Domain/range validation', 'SHACL consistency', 'Axiom analysis', 'Semantic reasoning'],
    pharmaExamples: [
      'DCAT (Data Catalog Vocabulary)',
      'PROV-O (Provenance Ontology)',
      'BFO (Basic Formal Ontology)',
      'OBI (Ontology for Biomedical Investigations)',
    ],
    fairImpact: 'Ontologies significantly improve interoperability through typed relationships and formal constraints, enabling machine-readable validation.',
  },
  knowledge_graph: {
    tier: 'knowledge_graph',
    label: 'Knowledge Graph',
    definition: 'An ontology populated with instance data (individuals) that represent real-world entities. Enables queries, inference, and cross-domain linking.',
    compassRole: 'DATA Compass detects instance data, evaluates completeness of typed individuals, and assesses readiness for graph-based analytics.',
    capabilities: ['Instance validation', 'Cross-referencing', 'SPARQL readiness', 'Inference support'],
    pharmaExamples: [
      'Clinical trial knowledge graphs (studies, sites, patients)',
      'Drug-target interaction graphs',
      'Regulatory submission knowledge bases',
      'Manufacturing process knowledge graphs',
    ],
    fairImpact: 'Knowledge graphs maximize all FAIR dimensions by combining formal semantics with queryable instance data, enabling full machine-actionability.',
  },
};

// =============================================================================
// COMPARISON TABLE DATA
// =============================================================================

export interface ComparisonRow {
  dimension: string;
  taxonomy: string;
  ontology: string;
  knowledgeGraph: string;
}

export const COMPARISON_DATA: ComparisonRow[] = [
  {
    dimension: 'Structure',
    taxonomy: 'Hierarchical tree (is-a)',
    ontology: 'Classes + typed properties',
    knowledgeGraph: 'Classes + properties + instances',
  },
  {
    dimension: 'Relationships',
    taxonomy: 'Parent-child only',
    ontology: 'Domain/range constrained',
    knowledgeGraph: 'Rich, cross-domain links',
  },
  {
    dimension: 'Instances',
    taxonomy: 'None',
    ontology: 'Schema only (TBox)',
    knowledgeGraph: 'Populated (ABox + TBox)',
  },
  {
    dimension: 'SHACL',
    taxonomy: 'Not applicable',
    ontology: 'Shape validation',
    knowledgeGraph: 'Instance + shape validation',
  },
  {
    dimension: 'Inference',
    taxonomy: 'Transitive hierarchy',
    ontology: 'OWL reasoning',
    knowledgeGraph: 'Full entailment',
  },
  {
    dimension: 'FAIR Coverage',
    taxonomy: 'Findability (partial)',
    ontology: 'F + I + R (strong)',
    knowledgeGraph: 'F + A + I + R (full)',
  },
  {
    dimension: 'Compass Role',
    taxonomy: 'Hierarchy health check',
    ontology: 'Full structural analysis',
    knowledgeGraph: 'Instance completeness audit',
  },
];

// =============================================================================
// TIER COLORS
// =============================================================================

export const TIER_COLORS: Record<KnowledgeTier, string> = {
  taxonomy: '#7EB8A4',
  ontology: '#C4A882',
  knowledge_graph: '#9B84C2',
};
